import { useState } from 'react';
import { useWriteGate } from '../lib/writeGate';
import { useFocusField } from './useFocusField';
import type { TransportType } from '../types';

/**
 * Shared chrome of the two config pages split out of the former
 * `Audio & Backline` page (v0.645): **Audio specs** (`ConfigAudioSpecsPage`)
 * and **Backline** (`ConfigBacklinePage`).
 *
 * Both pages persist through `useConfig` -> `PUT /api/bands/:id/rider/config`,
 * i.e. the rider CONTENT. So both share the SAME capability-aware write gate
 * (`content.rider`, never the blanket `canWrite` — that would freeze the very
 * crew member the capability is for), the SAME transport toggle and the SAME
 * deep-link focus (`?focus=<fieldId>` sent by the Rider Layout editor).
 *
 * Extracting them here keeps the two pages short AND guarantees a future fix
 * to the gate / focus applies to both at once — the whole point of the split
 * was to avoid two divergent copies of the plumbing.
 */
export interface ContentConfigPageChrome {
  /** Write predicate of THIS page's data: the `content.rider` capability. */
  canEditContent: boolean;
  /** True only when the page's own write predicate is denied. */
  readOnlyNote: boolean;
  transport: TransportType;
  setTransport: (t: TransportType) => void;
}

export function useContentConfigPage(): ContentConfigPageChrome {
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks this page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const [transport, setTransport] = useState<TransportType>('van');
  // The DOM is rendered immediately on these config pages, so focus right away.
  useFocusField(true);
  return { canEditContent, readOnlyNote, transport, setTransport };
}
